import { FC, useState } from 'react';
import { ITransaction } from '@features/workspace/types/banking';
import {
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { formatCurrencyWithSign, formatDate } from '@utils/formaters';

interface Props {
  transactions?: ITransaction[];
}

const Row: FC<ITransaction & { onCopy: () => void; showCopied: boolean }> = ({
  date,
  retrievalReferenceNumber,
  reference,
  amount,
  onCopy,
  showCopied,
}) => (
  <TableRow>
    <TableCell>{formatDate(date)}</TableCell>
    <TableCell
      sx={{
        color: amount >= 0 ? '#09351D !important' : '#550000 !important',
      }}
    >
      {formatCurrencyWithSign(amount)}
    </TableCell>
    <TableCell>{reference}</TableCell>
    <TableCell>{retrievalReferenceNumber}</TableCell>
    <TableCell>
      <Tooltip
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#168549',
              color: 'white',
              fontWeight: 'medium',
              fontSize: '14px',
              '& .MuiTooltip-arrow': {
                color: '#168549',
              },
            },
          },
        }}
        open={showCopied}
        placement="left"
        title="Copied!"
      >
        <i
          className="ri-file-copy-line"
          role="presentation"
          style={{ cursor: 'pointer', fontSize: '20px', color: '#0F253B' }}
          onClick={onCopy}
        />
      </Tooltip>
    </TableCell>
  </TableRow>
);

const TransactionsTable: FC<Props> = ({ transactions }) => {
  const [copiedRow, setCopiedRow] = useState<string | null>(null);

  const handleCopy = (transaction: ITransaction) => {
    const data = `${formatDate(transaction.date)} ${formatCurrencyWithSign(
      transaction.amount
    )} ${transaction.reference} ${transaction.retrievalReferenceNumber}`;

    navigator.clipboard.writeText(data);

    setCopiedRow(transaction.retrievalReferenceNumber);
    setTimeout(() => setCopiedRow(null), 2000);
  };

  return (
    <Table
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: '1px solid #E0E0E0',
        },
        [`& .MuiTableHead-root .${tableCellClasses.root}`]: {
          padding: '12px 16px',
          color: '#323C46',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          background: 'transparent',
        },
        [`& .MuiTableBody-root .${tableCellClasses.root}`]: {
          padding: '12px 16px',
          color: '#43505D',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '16px',
        },
        '& .MuiTableCell-root:first-of-type': {
          paddingLeft: '24px',
        },
        '& .MuiTableCell-root:last-of-type': {
          paddingRight: '24px',
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell width="15%">Date</TableCell>
          <TableCell width="15%">Amount</TableCell>
          <TableCell width="35%">Reference</TableCell>
          <TableCell width="30%">RRN Number</TableCell>
          <TableCell width="5%" />
        </TableRow>
      </TableHead>
      <TableBody>
        {transactions?.map((transaction) => (
          <Row
            key={transaction.retrievalReferenceNumber}
            {...transaction}
            showCopied={copiedRow === transaction.retrievalReferenceNumber}
            onCopy={() => handleCopy(transaction)}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default TransactionsTable;
